import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getCommissionSettings, saveCommissionSettings, getPoliciesPreview } from '../model/api.js';

const Section = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="border rounded-lg">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center p-4 text-left font-medium text-gray-800 bg-gray-50 hover:bg-gray-100">
                <span>{title}</span>
                <ChevronDownIcon className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && <div className="p-4 border-t">{children}</div>}
        </div>
    );
};

const TabSection = ({ title, children, tabs, activeTab, onTabChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="border rounded-lg">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center p-4 text-left font-medium text-gray-800 bg-gray-50 hover:bg-gray-100">
                <span>{title}</span>
                <ChevronDownIcon className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div className="p-4 border-t">
                    {/* Tab Navigation */}
                    <div className="border-b border-gray-200 mb-4">
                        <nav className="-mb-px flex space-x-8">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => onTabChange(tab.id)}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    {tab.name}
                                </button>
                            ))}
                        </nav>
                    </div>
                    {/* Tab Content */}
                    <div>
                        {tabs.find(tab => tab.id === activeTab)?.content}
                    </div>
                </div>
            )}
        </div>
    );
};

const CommissionManager = ({ onRecalculate, isRecalculating }) => {
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const saved = localStorage.getItem('commissionSelectedMonth');
        return saved || new Date().toISOString().slice(0, 7);
    });
    const [activeTab, setActiveTab] = useState('first-year-activation');
    const [policiesPreview, setPoliciesPreview] = useState(null);
    const [showPreview, setShowPreview] = useState(() => {
        return localStorage.getItem('commissionShowPreview') === 'true';
    });
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    const fetchSettings = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await getCommissionSettings();
            // API may return { ok, data } or raw settings object
            const raw = result && result.data ? result.data : result;
            // Normalize settings to ensure arrays exist before rendering
            const normalizeSettings = (s) => {
                const defaults = {
                    minPremiumThreshold: 15000,
                    yearlyRates: [{ id: 'yr-2', year: 2, rate: 0.04 }],
                    uplineConfig: [],
                    firstYearActivationCommissions: [],
                    activeUserBonuses: [],
                    premiumSlabs: [],
                    baseCommission: 0.01,
                    payoutControls: { approvalWaitingPeriodHours: 24 }
                };
                if (!s) return defaults;
                return {
                    ...defaults,
                    ...s,
                    yearlyRates: Array.isArray(s.yearlyRates) && s.yearlyRates.length ? s.yearlyRates : defaults.yearlyRates,
                    uplineConfig: Array.isArray(s.uplineConfig) ? s.uplineConfig : defaults.uplineConfig,
                    firstYearActivationCommissions: Array.isArray(s.firstYearActivationCommissions) ? s.firstYearActivationCommissions : defaults.firstYearActivationCommissions,
                    activeUserBonuses: Array.isArray(s.activeUserBonuses) ? s.activeUserBonuses : defaults.activeUserBonuses,
                    premiumSlabs: Array.isArray(s.premiumSlabs) ? s.premiumSlabs : defaults.premiumSlabs,
                    payoutControls: s.payoutControls || defaults.payoutControls
                };
            };

            setSettings(normalizeSettings(raw));
        } catch (error) {
            console.error("Failed to fetch commission settings", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // Reload preview if showPreview was true on mount
    useEffect(() => {
        if (showPreview && selectedMonth) {
            handleRecalculateClick();
        }
    }, []); // Empty dependency array to run only once on mount


    const handleSettingChange = (field, value) => {
        if (!settings) return;
        setSettings({ ...settings, [field]: value });
    };

    const handleNestedSettingChange = (section, index, field, value) => {
        if (!settings) return;
        const sectionData = settings[section];
        const updatedSection = [...sectionData];
        updatedSection[index] = { ...updatedSection[index], [field]: value };
        handleSettingChange(section, updatedSection);
    };

    const addListItem = (section) => {
        if (!settings) return;
        const newId = `${section}-${Date.now()}`;
        let newItem;
        switch (section) {
            case 'yearlyRates': {
                // Find the highest year in yearly rates (excluding year 1) and add 1
                const existingYears = settings.yearlyRates.filter(r => r.year >= 2).map(r => r.year);
                const nextYear = existingYears.length > 0 ? Math.max(...existingYears) + 1 : 2;
                newItem = { id: newId, year: nextYear, rate: 0 };
                break;
            }
            case 'activeUserBonuses': newItem = { id: newId, userCount: settings.activeUserBonuses.length, bonusRate: 0 }; break;
            case 'uplineConfig': newItem = { id: newId, level: settings.uplineConfig.length, name: `Level ${settings.uplineConfig.length}`, rate: 0 }; break;
            case 'premiumSlabs': newItem = { id: newId, minPremium: 0, maxPremium: null, rate: 1.0 }; break;
            case 'firstYearActivationCommissions': newItem = {
                id: newId,
                activeCount: settings.firstYearActivationCommissions?.length || 0,
                commissionRate: 0.02,
                description: `${settings.firstYearActivationCommissions?.length || 0} active members`
            }; break;
        }
        setSettings({ ...settings, [section]: [...(settings[section] || []), newItem] });
    };

    const removeListItem = (section, index) => {
        if (!settings) return;
        const sectionData = settings[section];
        handleSettingChange(section, sectionData.filter((_, i) => i !== index));
    };

    const handleSaveChanges = async () => {
        if (!settings) return;
        const success = await saveCommissionSettings(settings);
        if (success) {
            toast.success('Settings saved successfully!');
            // Refetch settings to update UI
            await fetchSettings();
        } else {
            toast.error('Failed to save settings.');
        }
    };

    const handleRecalculateClick = async () => {
        if (!selectedMonth) return toast.error("Please select a month.");

        // Show policies preview directly
        setIsLoadingPreview(true);
        try {
            // Convert month to date range for the entire month
            const [year, month] = selectedMonth.split('-').map(Number);
            const startDate = `${year}-${month.toString().padStart(2, '0')}-01`;
            const endDate = `${year}-${month.toString().padStart(2, '0')}-${new Date(year, month, 0).getDate().toString().padStart(2, '0')}`;

            const preview = await getPoliciesPreview(startDate, endDate);
            // No manual filtering - show all policies from backend

            setPoliciesPreview(preview);
            setShowPreview(true);
            // Persist state to localStorage
            localStorage.setItem('commissionShowPreview', 'true');
            localStorage.setItem('commissionSelectedMonth', selectedMonth);
        } catch (error) {
            console.error('Error fetching policies preview:', error);
            toast.error('Failed to load policies preview. Please try again.');
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const handleMonthChange = (month) => {
        setSelectedMonth(month);
        localStorage.setItem('commissionSelectedMonth', month);
    };

    if (isLoading) return <div className="p-4 text-center">Loading Commission Settings...</div>;
    if (!settings) return <div className="p-4 text-center text-red-500">Failed to load settings.</div>;

    return (
        <div className="bg-white p-6 rounded-lg shadow-md space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Commission Management</h3>

            <div className="space-y-4">
                <TabSection
                    title="Base & Yearly Commission"
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    tabs={[
                        {
                            id: 'first-year-activation',
                            name: '1st Year (Activation-based)',
                            content: (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Minimum Premium Threshold (₹)</label>
                                        <input
                                            type="number"
                                            value={settings.minPremiumThreshold || 15000}
                                            onChange={e => handleSettingChange('minPremiumThreshold', parseFloat(e.target.value))}
                                            className="mt-1 p-2 border rounded-md w-full"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Only policies above this amount count toward active member status for first year commissions.</p>
                                    </div>

                                    <h4 className="font-medium">First Year Activation Commission Rates</h4>
                                    <p className="text-sm text-gray-600">Configure commission rates for leaders based on team activation status in their first year.</p>

                                    <div className="space-y-3">
                                        <h5 className="font-medium">Edit Commission Rates by Active Member Count:</h5>
                                        {settings.firstYearActivationCommissions?.map((rule, index) => (
                                            <div key={rule.id} className="flex items-center space-x-2 p-3 border rounded-md">
                                                <div className="flex-1">
                                                    <label className="block text-sm font-medium text-gray-700">Active Members</label>
                                                    <input
                                                        type="number"
                                                        placeholder="Active Count"
                                                        value={rule.activeCount}
                                                        onChange={e => handleNestedSettingChange('firstYearActivationCommissions', index, 'activeCount', parseInt(e.target.value))}
                                                        className="mt-1 p-2 border rounded-md w-full"
                                                        min="0"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <label className="block text-sm font-medium text-gray-700">Commission Rate (%)</label>
                                                    <input
                                                        type="number"
                                                        placeholder="Rate (%)"
                                                        value={rule.commissionRate * 100}
                                                        onChange={e => handleNestedSettingChange('firstYearActivationCommissions', index, 'commissionRate', parseFloat(e.target.value) / 100)}
                                                        className="mt-1 p-2 border rounded-md w-full"
                                                        step="0.1"
                                                        min="0"
                                                        max="100"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <label className="block text-sm font-medium text-gray-700">Description</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Description"
                                                        value={rule.description || ''}
                                                        onChange={e => handleNestedSettingChange('firstYearActivationCommissions', index, 'description', e.target.value)}
                                                        className="mt-1 p-2 border rounded-md w-full"
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => removeListItem('firstYearActivationCommissions', index)}
                                                    className="text-red-500 p-2 mt-6 hover:bg-red-50 rounded"
                                                    title="Remove this tier"
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        ))}
                                        <button onClick={() => addListItem('firstYearActivationCommissions')} className="text-sm text-indigo-600 hover:text-indigo-800">
                                            + Add Activation Tier
                                        </button>
                                    </div>

                                    <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mt-4">
                                        <h5 className="font-medium text-blue-800 mb-2">Current Rate Summary:</h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {settings.firstYearActivationCommissions?.slice(0, 2).map((rule, index) => (
                                                <div key={rule.id}>
                                                    <label className="block text-sm font-medium text-blue-800">
                                                        {rule.activeCount === 0 ? 'Not Activated (0 active members)' : `Activated (${rule.activeCount}+ active members)`}
                                                    </label>
                                                    <div className="text-2xl font-bold text-blue-600">{(rule.commissionRate * 100).toFixed(1)}%</div>
                                                    <p className="text-xs text-blue-700">{rule.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                        <p className="text-sm text-blue-800">
                                            <strong>How it works:</strong> Commission rates are determined by the number of active team members with qualifying policies (above ₹{settings.minPremiumThreshold || 15000} total premium). The system matches the leader's active member count to the appropriate tier above and applies that commission rate to Year 1 revenue.
                                        </p>
                                    </div>
                                </div>
                            )
                        },
                        {
                            id: 'yearly-rates',
                            name: '2nd+ Years (Yearly Rates)',
                            content: (
                                <div className="space-y-4">
                                    <h4 className="font-medium">Yearly Rates (2nd Year and Beyond)</h4>
                                    <p className="text-sm text-gray-600">Configure commission rates for renewal years (2nd year onwards). Note: 1st year commissions use activation-based formulas.</p>
                                    {settings.yearlyRates.filter(rule => rule.year >= 2).map((rule, index) => {
                                        // Find the actual index in the original array
                                        const originalIndex = settings.yearlyRates.findIndex(r => r.id === rule.id);
                                        return (
                                            <div key={rule.id} className="flex items-center space-x-2">
                                                <input type="number" placeholder="Year" value={rule.year} onChange={e => handleNestedSettingChange('yearlyRates', originalIndex, 'year', parseInt(e.target.value))} className="p-2 border rounded-md w-1/3" min="2" />
                                                <input type="number" placeholder="Rate (%)" value={rule.rate * 100} onChange={e => handleNestedSettingChange('yearlyRates', originalIndex, 'rate', parseFloat(e.target.value) / 100)} className="p-2 border rounded-md w-1/3" step="0.1" min="0" max="100" />
                                                <button onClick={() => removeListItem('yearlyRates', originalIndex)} className="text-red-500 p-2">&times;</button>
                                            </div>
                                        );
                                    })}
                                    <button onClick={() => addListItem('yearlyRates')} className="text-sm text-indigo-600">+ Add Year</button>
                                </div>
                            )
                        }
                    ]}
                />


                <Section title="Hierarchy & Active User Commission">
                    <div className="space-y-4">
                        <h4 className="font-medium">Leader/Upline Hierarchy Commission (% of policy commission)</h4>
                        <p className="text-sm text-gray-600">Configure commission rates for each level in the hierarchy. Commissions are distributed up the chain based on these rates.</p>

                        <div className="bg-blue-50 p-4 rounded-md mb-4">
                            <h5 className="font-medium text-blue-800 mb-2">Current Hierarchy Structure:</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                                {settings.uplineConfig.map((rule, index) => (
                                    <div key={rule.id} className={`p-2 rounded ${index === 0 ? 'bg-green-100 text-green-800' :
                                        index === 1 ? 'bg-blue-100 text-blue-800' :
                                            index === 2 ? 'bg-purple-100 text-purple-800' :
                                                'bg-orange-100 text-orange-800'
                                        }`}>
                                        <div className="font-medium">{rule.name}</div>
                                        <div>{rule.rate * 100}% commission</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <h5 className="font-medium">Edit Hierarchy Levels:</h5>
                            {settings.uplineConfig.map((rule, index) => (
                                <div key={rule.id} className="flex items-center space-x-2 p-3 border rounded-md">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700">Level Name</label>
                                        <input
                                            type="text"
                                            placeholder="Level Name"
                                            value={rule.name}
                                            onChange={e => handleNestedSettingChange('uplineConfig', index, 'name', e.target.value)}
                                            className="mt-1 p-2 border rounded-md w-full"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700">Commission Rate (%)</label>
                                        <input
                                            type="number"
                                            placeholder="Rate (%)"
                                            value={rule.rate * 100}
                                            onChange={e => handleNestedSettingChange('uplineConfig', index, 'rate', parseFloat(e.target.value) / 100)}
                                            className="mt-1 p-2 border rounded-md w-full"
                                            step="0.1"
                                            min="0"
                                            max="100"
                                        />
                                    </div>
                                    <button
                                        onClick={() => removeListItem('uplineConfig', index)}
                                        className="text-red-500 p-2 mt-6 hover:bg-red-50 rounded"
                                        title="Remove this level"
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}
                        </div>

                        <button onClick={() => addListItem('uplineConfig')} className="text-sm text-indigo-600 hover:text-indigo-800">
                            + Add Hierarchy Level
                        </button>

                        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                            <p className="text-sm text-gray-600">
                                <strong>How it works:</strong> When a leader earns commission from their team's performance,
                                the system automatically distributes portions to their upline leaders based on these configured rates.
                                Commissions cascade up the hierarchy with each level receiving a percentage of the previous level's commission.
                                For example, if a Leader 3 earns ₹2,400, Leader 2 gets 10% = ₹240, Leader 1 gets 10% of ₹240 = ₹24, and Super Admin gets 10% of ₹24 = ₹2.40.
                            </p>
                        </div>
                    </div>
                </Section>



            </div>

            <div className="flex justify-end pt-4 border-t">
                <button onClick={handleSaveChanges} className="px-6 py-2 bg-indigo-600 text-white rounded-md shadow-sm hover:bg-indigo-700 transition-colors">
                    Save All Settings
                </button>
            </div>

            <div className="border-t pt-6">
                <h4 className="text-md font-medium text-gray-700 mb-2">View Policy Details for Selected Month</h4>
                <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-600">Select Month:</label>
                        <input
                            type="month"
                            value={selectedMonth}
                            onChange={e => handleMonthChange(e.target.value)}
                            className="p-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <button onClick={handleRecalculateClick} className="px-6 py-2 bg-green-500 text-white rounded-md shadow-sm hover:bg-green-600 transition-colors disabled:bg-gray-400" disabled={isRecalculating || isLoadingPreview}>
                        {isLoadingPreview
                            ? 'Loading Policy Details...'
                            : 'View Policy Details'
                        }
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    Select a month to view policy details that will be included in commission calculations for that month.
                </p>

                {/* Policies Preview Section */}
                {showPreview && (
                    <div className="mt-6 border-t pt-6">
                        <div className="flex justify-between items-center mb-4">
                            <h5 className="text-lg font-medium text-gray-800">Policy Details for {selectedMonth}</h5>
                            <button
                                onClick={() => {
                                    setShowPreview(false);
                                    localStorage.setItem('commissionShowPreview', 'false');
                                }}
                                className="text-gray-500 hover:text-gray-700 text-sm underline"
                            >
                                Hide Details
                            </button>
                        </div>

                        {isLoadingPreview ? (
                            <div className="text-center py-8">Loading policies preview...</div>
                        ) : policiesPreview ? (
                            <div className="space-y-4">
                                {/* Summary */}
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h6 className="font-medium text-blue-800 mb-2">Summary</h6>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                                        <div>
                                            <span className="font-medium">Total Policies:</span> {policiesPreview.totalPolicies}
                                        </div>
                                        <div>
                                            <span className="font-medium">Qualifying Policies:</span> {policiesPreview.qualifyingPolicies}
                                        </div>
                                        <div>
                                            <span className="font-medium">Total Premium (Qualifying):</span> ₹{policiesPreview.totalMonthlyPremium?.toLocaleString()}
                                        </div>
                                        <div>
                                            <span className="font-medium">Total Premium (All):</span> ₹{policiesPreview.totalAllPoliciesPremium?.toLocaleString()}
                                        </div>
                                    </div>
                                </div>


                                {/* Policy Details */}
                                <div className="mt-6">
                                    {policiesPreview.policies && policiesPreview.policies.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full text-sm border">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="text-left py-3 px-4 border-b">Policy No</th>
                                                        <th className="text-left py-3 px-4 border-b">Advisor</th>
                                                        <th className="text-left py-3 px-4 border-b">Leader</th>
                                                        <th className="text-center py-3 px-4 border-b">Policy Duration</th>
                                                        <th className="text-right py-3 px-4 border-b">Total Premium</th>
                                                        <th className="text-center py-3 px-4 border-b">Qualifies</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {policiesPreview.policies.map((policy, index) => (
                                                        <tr key={index} className="border-b hover:bg-gray-50">
                                                            <td className="py-3 px-4">{policy.policyNo}</td>
                                                            <td className="py-3 px-4">{policy.advisorName}</td>
                                                            <td className="py-3 px-4">{policy.leaderName}</td>
                                                            <td className="py-3 px-4 text-center">{policy.durationYears} Year{policy.durationYears !== 1 ? 's' : ''}</td>
                                                            <td className="py-3 px-4 text-right">₹{policy.sumInsured?.toLocaleString()}</td>
                                                            <td className="py-3 px-4 text-center">
                                                                <span className={`px-2 py-1 rounded text-xs ${policy.qualifiesForActivation
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {policy.qualifiesForActivation ? 'Yes' : 'No'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            {/* Manual Commission Recalculation Button */}
                                            <div className="mt-4 flex justify-center">
                                                <button
                                                    onClick={() => {
                                                        if (onRecalculate) {
                                                            const [year, month] = selectedMonth.split('-');
                                                            onRecalculate(`${year}-${month}`);
                                                        }
                                                    }}
                                                    disabled={isRecalculating}
                                                    className="px-6 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                                                >
                                                    {isRecalculating ? 'Recalculating Commissions...' : 'Recalculate Commissions for This Month'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-center py-4">No policies found in the selected period.</p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-red-500">
                                Failed to load policies preview
                            </div>
                        )}
                    </div>
                )}
            </div>

        </div>
    );
};

const ChevronDownIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

export default CommissionManager;